import { IUser, Unsubscribe } from "@home-health-notify/domain";
import * as Sentry from "@sentry/react";
import {
  AddMembersProvider,
  ApiErrorHandler,
  ChatInfoProvider,
  ChatProvider,
  DomainContext,
  OrganizationContext,
  UserDirectoryProvider,
  useTranslation,
} from "hhn-commons";
import { useSnackbar } from "notistack";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { userTracking } from "../analytics/userTracking";
import { AdvanceSearchDialogProvider } from "../context/AdvanceSearchDialogContext";
import { DocumentMediaViewerContext } from "../context/DocumentMediaViewerContext";
import { LeftDrawerProvider } from "../context/LeftDrawerProvider";
import { NotificationProvider } from "../context/NotificationProvider";
import { RightDrawerProvider } from "../context/RightDrawerProvider";
import LeftDrawer from "../layout/LeftDrawer";
import MainLayout from "../layout/MainLayout";
import RightDrawer from "../layout/RightDrawer";
import Loader from "../loader/Loader";
import DocumentMediaViewer from "../message/documentViewer/DocumentMediaViewer";
import { isValidSubRoute, ROUTES } from "../routes";
import { consumeUnconfirmedForward } from "../utils/unconfirmedForwardNotice";
import {
  clearOrganizationId,
  storeOrganizationId,
} from "../utils/organizationIdStorage";
import NotFound from "../notFound/NotFound";
import ChatRoutes from "./ChatRoutes";
import { HomeContainer } from "./HomePage.style";
import { ModalManager } from "./ModalManager";

interface IHomePageProps {
  onLogout: () => void;
}

// Using HomePageWrapper to remount component tree on orgId change using key prop
export function HomePageWrapper(props: IHomePageProps) {
  const { orgId } = useParams();
  return <HomePage key={orgId} {...props} />;
}

function HomePage({ onLogout }: IHomePageProps) {
  const { domain } = useContext(DomainContext);
  const { setSelectedOrgUser, selectedOrgUser, unSetSelectedOrgUser } =
    useContext(OrganizationContext);
  const { message, mediaList, onDocumentMediaDialogClose } = useContext(
    DocumentMediaViewerContext,
  );
  const { orgId, "*": splat } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const user: IUser | null = domain.user;

  useEffect(() => {
    // A forward that went unconfirmed before the offline overlay reloaded the
    // page left a note behind; this is the first chance to show it.
    const unconfirmedForward = consumeUnconfirmedForward();
    if (unconfirmedForward) {
      enqueueSnackbar(
        t("forwardTo.forwardUnconfirmedOnReturn", {
          ns: "chat",
          count: unconfirmedForward.contactCount,
        }),
        { variant: "warning", persist: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let removeErrorListener: Unsubscribe;
    let removeSlowSendListener: Unsubscribe;
    if (selectedOrgUser) {
      removeErrorListener = selectedOrgUser.addSocketResponseErrorListener(
        (errorMessage: any) => {
          enqueueSnackbar(errorMessage, {
            variant: "error",
          });
        },
      );
      removeSlowSendListener = selectedOrgUser.addSlowMessageSendListener(
        (data: any) => {
          const timedOutNoAck = data.timedOut && data.ackReceived === false;
          Sentry.captureEvent({
            message: timedOutNoAck
              ? "Message send timed out - no SEND_MESSAGE ack received"
              : data.timedOut
                ? "Message send timed out - no CHAT_MESSAGE received"
                : "Slow message send detected",
            level: data.timedOut ? "error" : "warning",
            fingerprint: [
              timedOutNoAck
                ? "message-send-no-ack"
                : data.timedOut
                  ? "message-send-timeout"
                  : "slow-message-send",
            ],
            extra: {
              messageId: data.messageId,
              durationMs: data.durationMs,
              chatId: data.chatId,
              socketConnected: data.socketConnected,
              socketId: data.socketId,
              timedOut: data.timedOut ?? false,
              ackReceived: data.ackReceived ?? null,
            },
          });
        },
      );
    }
    return () => {
      removeErrorListener?.();
      removeSlowSendListener?.();
    };
  }, [selectedOrgUser, enqueueSnackbar]);
  const [isAllOrgDataLoaded, setIsAllOrgDataLoaded] = useState(false);

  const destroyPromiseRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    async function fetchOrgData() {
      try {
        // Wait for any ongoing destroy to complete before re-initializing
        if (destroyPromiseRef.current) {
          await destroyPromiseRef.current;
          destroyPromiseRef.current = null;
        }

        setIsAllOrgDataLoaded(false);
        if (user && orgId) {
          const orgUsers = user.getActiveOrganizations();
          const isValidOrgId = orgUsers.some(
            orgUser => orgUser.getOrganization().organizationId === orgId,
          );
          if (!isValidOrgId) {
            enqueueSnackbar("Organization not found or expired", {
              variant: "error",
            });
            clearOrganizationId(); // Clear invalid org id from local storage
            navigate(ROUTES.ORGANIZATIONS);
            return;
          }
          storeOrganizationId(orgId);
          const orgUser = user.getOrganizationById(orgId);
          await setSelectedOrgUser(orgUser);
        } else {
          await setSelectedOrgUser(undefined);
        }
      } catch (e) {
        ApiErrorHandler(e);
        navigate(ROUTES.ORGANIZATIONS);
      } finally {
        setIsAllOrgDataLoaded(true);
      }
    }

    fetchOrgData();

    return () => {
      if (user && orgId) {
        const orgUser = user.getOrganizationById(orgId);
        if (orgUser) {
          destroyPromiseRef.current = Promise.resolve(
            unSetSelectedOrgUser(orgUser),
          );
        }
      }
    };
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [orgId]);

  useEffect(() => {
    if (selectedOrgUser) {
      const currentUser = selectedOrgUser.userDirectory.currentUserProfile;
      Sentry.setUser({
        id: currentUser.id,
        name: currentUser.name,
        email: currentUser.email,
      });
    }
  }, [selectedOrgUser]);

  const currentOrganization = selectedOrgUser?.getOrganization();
  const userProfile = currentOrganization?.userProfile;
  useEffect(() => {
    if (!userProfile) return;

    userTracking.initializeUser(
      userProfile,
      currentOrganization?.organizationId,
    );

    const handleBeforeUnload = (): void => {
      userTracking.endSession();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      userTracking.stopViewportTracking();
    };
  }, [userProfile]);
  if (!isValidSubRoute(splat)) {
    return (
      <NotFound
        buttonLabel="Go to Home"
        navigateTo={`${ROUTES.ORGANIZATIONS}/${orgId}/${ROUTES.CHATS}`}
      />
    );
  }

  return (
    <>
      {isAllOrgDataLoaded ? (
        <UserDirectoryProvider>
          <ChatInfoProvider>
            <ChatProvider>
              <NotificationProvider>
                <LeftDrawerProvider>
                  <RightDrawerProvider>
                    <AddMembersProvider>
                      <AdvanceSearchDialogProvider>
                        {selectedOrgUser && (
                          <>
                            <MainLayout onLogout={onLogout}>
                              <HomeContainer>
                                <ChatRoutes />
                              </HomeContainer>
                            </MainLayout>
                            <RightDrawer />
                            <LeftDrawer />
                            <ModalManager />
                          </>
                        )}
                        {message && (
                          <DocumentMediaViewer
                            dialog={true}
                            onDocumentMediaDialogClose={
                              onDocumentMediaDialogClose || (() => {})
                            }
                            selectedMedia={message}
                            mediaList={mediaList}
                          />
                        )}
                      </AdvanceSearchDialogProvider>
                    </AddMembersProvider>
                  </RightDrawerProvider>
                </LeftDrawerProvider>
              </NotificationProvider>
            </ChatProvider>
          </ChatInfoProvider>
        </UserDirectoryProvider>
      ) : (
        <Loader />
      )}
    </>
  );
}

export default HomePage;
